import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
} from '@mui/material';
import { format as formatDate } from 'date-fns';
import { de } from 'date-fns/locale';
import type React from 'react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { submitRevocation as submitRevocationAction } from '../../actions/user.actions';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import WithdrawalIcon from '../../assets/subscription/withdrawal-plan.svg';
import { dateTimeFormat } from '../../constants';
import { NotificationContext } from '../../state/NotificationContextProvider';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import useAction from '../../utils/useAction';
import useScreenSize from '../../utils/useScreenSize';
import DialogActions from '../common/DialogActions';

interface StripeWithdrawalDialogProps {
	open: boolean;
	onClose: () => void;
	// The withdrawing user's email address (contract identifier).
	email: string;
	// Pre-formatted subscription start date (from the Stripe period start).
	startDate: string;
}

// SM-254: standalone 14-day right-of-withdrawal (Widerruf) dialog for Stripe
// users. It mirrors the legacy WithdrawSubDialog's presentation (icon + legal
// notice + read-only contract fields) but submits through kimuno's revocation
// intake (`submitRevocation`) instead of the legacy WIDERRUF experiment hack.
// The legacy WithdrawSubDialog stays Zuora-only; this is the Stripe entry point,
// surfaced next to the "Abo verwalten" (customer portal) button in SubscriptionInfo.
const StripeWithdrawalDialog: React.FC<StripeWithdrawalDialogProps> = ({
	open,
	onClose,
	email,
	startDate,
}) => {
	const { t } = useTranslation();
	const { isXsScreen } = useScreenSize();
	const { showNotification } = useContext(NotificationContext);
	const submitRevocation = useAction(submitRevocationAction);
	const [submitting, setSubmitting] = useState(false);

	const handleWithdrawalRequest = async () => {
		if (submitting) return;
		setSubmitting(true);
		const submissionDateTime = formatDate(new Date(), dateTimeFormat, {
			locale: de,
		});

		try {
			// Record a kimuno-tracked revocation request; support later executes the
			// immediate cancel + full refund and kimuno sends the confirmation email.
			await submitRevocation();

			showNotification({
				message: t('Your withdrawal was submitted'),
				description: t(
					'We received your withdrawal on {{submissionDateTime}}. You will receive a confirmation by email.',
					{ submissionDateTime }
				),
				severity: 'success',
			});
			onClose();
		} catch (error) {
			showNotification({
				message: t('Your withdrawal was not successful'),
				severity: 'error',
			});
			notifyBugsnagHandledError(error as Error);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog onClose={submitting ? undefined : onClose} open={open} fullWidth maxWidth="lg">
			<DialogTitle
				component="div"
				sx={{
					paddingTop: spaces.lg,
					paddingBottom: spaces.lg,
					borderBottom: `1px solid ${palette.elevation.outlined}`,
					display: 'flex',
					flexDirection: 'column',
					gap: spaces.xs,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography variant="h5" color={palette.text.primary}>
						{t('Withdraw contract')}
					</Typography>
					<Box>
						<IconButton
							color="default"
							size="small"
							onClick={onClose}
							disabled={submitting}
							data-testid="close-button"
							aria-label={t('Close')}
						>
							<CloseIcon />
						</IconButton>
					</Box>
				</Box>
			</DialogTitle>
			<DialogContent sx={{ p: 0 }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: isXsScreen ? 'column' : 'row',
						alignItems: 'flex-start',
						flex: '1 0 0',
						alignSelf: 'stretch',
					}}
				>
					{!isXsScreen && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								alignSelf: 'center',
								'& svg': {
									width: '400px',
									height: '300px',
								},
							}}
						>
							<WithdrawalIcon />
						</Box>
					)}
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							gap: spaces.lg,
							padding: spaces.lg,
							flex: '1 0 0',
							alignSelf: 'stretch',
							alignItems: 'flex-start',
						}}
					>
						<Typography variant="body2" sx={{ color: palette.text.secondary }}>
							{t(
								'Here you can declare the withdrawal of your contract concluded online.' +
									' After submission, you will receive a confirmation by email.'
							)}
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: spaces.md,
								alignSelf: 'stretch',
							}}
						>
							<TextField
								disabled
								label={t('Email address')}
								value={email ?? ''}
								fullWidth
								slotProps={{
									input: { readOnly: true },
									htmlInput: { 'data-testid': 'withdrawal-email' },
								}}
							/>
							<TextField
								disabled
								label={t('Start date')}
								value={startDate}
								fullWidth
								slotProps={{
									input: { readOnly: true },
									htmlInput: { 'data-testid': 'withdrawal-start-date' },
								}}
							/>
						</Box>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions
				primaryButton={{
					text: t('Confirm withdrawal'),
					color: 'primary',
					variant: 'contained',
					onClick: handleWithdrawalRequest,
					loading: submitting,
				}}
				secondaryButton={{
					text: t('Cancel'),
					onClick: onClose,
					disabled: submitting,
				}}
			/>
		</Dialog>
	);
};

export default StripeWithdrawalDialog;
